/**
 * Vehicle settlement task metadata (IAD step 3 — Figma frames 44, 47, 50, 53, 56).
 * Checklist IDs must stay in sync with settlementFlowConfig (iad-3).
 */

export type VehicleTaskKind = 'secure' | 'insurance' | 'registration' | 'maintenance' | 'storage';

export type VehicleFieldType = 'text' | 'textarea' | 'select';

export interface VehicleFieldDef {
  key: string;
  label: string;
  type: VehicleFieldType;
  options?: { value: string; label: string }[];
}

export interface VehicleTaskDefinition {
  kind: VehicleTaskKind;
  frameId: string;
  route: string;
  title: string;
  description: string;
  checklistItemId: string;
  fields: VehicleFieldDef[];
}

export const VEHICLE_TASK_DEFINITIONS: Record<VehicleTaskKind, VehicleTaskDefinition> = {
  secure: {
    kind: 'secure',
    frameId: 'frame-44',
    route: '/frame-44',
    title: 'Secure vehicles',
    description:
      'Document where each vehicle is kept, who has keys, and any security devices so nothing is lost or driven without authorization.',
    checklistItemId: 'iad-3-check-secure',
    fields: [
      { key: 'securedLocation', label: 'Secured location (address, garage bay, lot #)', type: 'text' },
      { key: 'keysLocation', label: 'Keys / fobs location', type: 'text' },
      { key: 'spareKeys', label: 'Spare keys located?', type: 'select', options: [
        { value: '', label: '—' },
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unknown', label: 'Unknown' },
      ]},
      { key: 'alarmNotes', label: 'Alarm, immobilizer, or garage access notes', type: 'textarea' },
      { key: 'odometer', label: 'Odometer (approx.)', type: 'text' },
    ],
  },
  insurance: {
    kind: 'insurance',
    frameId: 'frame-47',
    route: '/frame-47',
    title: 'Vehicle insurance',
    description:
      'Capture insurer details and policy status for each vehicle. Use this when notifying carriers or opening claims.',
    checklistItemId: 'iad-3-check-insurance',
    fields: [
      { key: 'insurer', label: 'Insurance company', type: 'text' },
      { key: 'policyNumber', label: 'Policy number', type: 'text' },
      { key: 'agentOrPhone', label: 'Agent / claims phone', type: 'text' },
      { key: 'coverageType', label: 'Coverage type (comprehensive, liability only, etc.)', type: 'text' },
      { key: 'renewalOrExpiry', label: 'Renewal or expiration date', type: 'text' },
      { key: 'claimNotes', label: 'Claim or cancellation notes', type: 'textarea' },
    ],
  },
  registration: {
    kind: 'registration',
    frameId: 'frame-50',
    route: '/frame-50',
    title: 'Vehicle registration & title',
    description:
      'Record title location, lienholder, and registration details. You will need this for DMV notifications and eventual transfer.',
    checklistItemId: 'iad-3-check-registration',
    fields: [
      { key: 'titleLocation', label: 'Physical title location', type: 'text' },
      { key: 'registrationState', label: 'Registration state / province', type: 'text' },
      { key: 'plateNumber', label: 'License plate', type: 'text' },
      { key: 'registrationExpires', label: 'Registration expiration', type: 'text' },
      { key: 'lienholder', label: 'Lienholder (if any)', type: 'text' },
      { key: 'dmvNotes', label: 'DMV or titling notes', type: 'textarea' },
    ],
  },
  maintenance: {
    kind: 'maintenance',
    frameId: 'frame-53',
    route: '/frame-53',
    title: 'Vehicle maintenance',
    description:
      'Track mechanical condition and service needs so vehicles stay preserved and safe while the estate is administered.',
    checklistItemId: 'iad-3-check-maintenance',
    fields: [
      { key: 'lastService', label: 'Last known service date / mileage', type: 'text' },
      { key: 'battery', label: 'Battery / jump status', type: 'text' },
      { key: 'tires', label: 'Tires (condition or flat)', type: 'text' },
      { key: 'fuelLevel', label: 'Fuel level', type: 'select', options: [
        { value: '', label: '—' },
        { value: 'full', label: 'Full' },
        { value: 'partial', label: 'Partial' },
        { value: 'empty', label: 'Empty / unknown' },
      ]},
      { key: 'knownIssues', label: 'Known mechanical issues', type: 'textarea' },
      { key: 'nextService', label: 'Recommended next service', type: 'textarea' },
    ],
  },
  storage: {
    kind: 'storage',
    frameId: 'frame-56',
    route: '/frame-56',
    title: 'Vehicle storage decisions',
    description:
      'Decide interim storage, who may access each vehicle, and whether sale or donation is being considered.',
    checklistItemId: 'iad-3-check-storage',
    fields: [
      { key: 'interimPlan', label: 'Interim plan', type: 'select', options: [
        { value: '', label: '—' },
        { value: 'keep-garage', label: 'Keep at residence / garage' },
        { value: 'third-party', label: 'Third-party storage' },
        { value: 'beneficiary', label: 'With beneficiary / family' },
        { value: 'sell', label: 'Prepare for sale' },
        { value: 'donate', label: 'Prepare for donation' },
        { value: 'undecided', label: 'Undecided' },
      ]},
      { key: 'storageLocation', label: 'Storage location (if not at residence)', type: 'text' },
      { key: 'accessWho', label: 'Who may access or move the vehicle', type: 'text' },
      { key: 'insuranceWhileStored', label: 'Insurance while stored (carrier / notes)', type: 'text' },
      { key: 'decisionNotes', label: 'Executor notes', type: 'textarea' },
    ],
  },
};

export const VEHICLE_TASK_ORDER: VehicleTaskKind[] = [
  'secure',
  'insurance',
  'registration',
  'maintenance',
  'storage',
];

export interface VehicleFormRow {
  id: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  licensePlate: string;
  /** Task-specific field values keyed by VehicleFieldDef.key */
  values: Record<string, string>;
}
